import React, { useMemo, useState } from 'react';
import gql from 'graphql-tag';
import { useMutation, useQuery } from '@apollo/react-hooks';
import Loading from '../../../components/Loading';
import { FaInfoCircle } from 'react-icons/fa';
import { format, parseISO } from 'date-fns';
import Modal from '../../../components/Modal';
import { useFormik } from 'formik';
import Checkbox from '../../../components/form/Checkbox';
import StatusButton from '../../../components/StatusButton';
import classNames from 'classnames';
import { array, object, string } from 'yup';
import { usePermissions } from '../../../hooks/usePermissions';
import { userGroups } from '../../../context/AuthContext';

const PUSH_PARTNERS = gql`
    query {
        pushApiPartners {
            id
            name
        }
    }
`;
const PUSH_API_STATUS = gql`
    query ($contentId: ID!) {
        pushApiTitleStatus(contentId: $contentId) {
            partners {
                assets {
                    summary
                    last_sent
                }
                metadata {
                    summary
                    last_sent
                }
                target_id
                partner_id
            }
            messages {
                is_error
                message
                path
            }
        }
    }
`;

const PUSH_TITLE_MUTATION = gql`
    mutation PushTitle($contentId: ID!, $partnerIds: [ID!]) {
        pushApiPushTitle(contentId: $contentId, partnerIds: $partnerIds)
    }
`;

const AddToQueue = ({ contentId, onCancel = () => {} }) => {
    const [submitSuccess, setSubmitSuccess] = useState(null);
    const isSpotifyUser = usePermissions(userGroups.SPOTIFY);
    const { loading: partnersLoading, data: allPartners } = useQuery(PUSH_PARTNERS, { skip: isSpotifyUser });
    const { loading: dataLoading, data: pushApiStatus } = useQuery(PUSH_API_STATUS, {
        variables: {
            contentId
        }
    });

    let partners = useMemo(() => {
        if (!allPartners) return [];
        let ids = [...new Set(pushApiStatus?.pushApiTitleStatus?.partners.map((p) => p.partner_id))];
        return ids.map((id) => allPartners?.pushApiPartners.find((p) => p.id === id));
    }, [allPartners, pushApiStatus.pushApiTitleStatus.partners]);

    const partnerIds = useMemo(() => partners?.map((p) => p.id), [partners]);

    const [pushTitle, { loading: isPushing }] = useMutation(PUSH_TITLE_MUTATION);

    const { values, errors, touched, handleChange, handleSubmit, resetForm, setFieldValue } = useFormik({
        initialValues: {
            contentId,
            partnerIds: isSpotifyUser ? ['spotify'] : []
        },
        validationSchema: object().shape({
            contentId: string().required(),
            partnerIds: array(string()).min(1, 'You must choose at least 1 partner.')
        }),
        onSubmit: async (values) => {
            try {
                await pushTitle({ variables: values });
                setSubmitSuccess('success');
                setTimeout(() => {
                    resetForm();
                    setSubmitSuccess(null);
                    onCancel();
                }, 3000);
            } catch {
                setSubmitSuccess('error');
            }
        },
        enableReinitialize: true
    });

    const handleReset = () => {
        resetForm();
        onCancel();
    };
    const handleSelectAll = (e) => {
        e.preventDefault();
        setFieldValue('partnerIds', partnerIds);
    };
    const handleDeselectAll = (e) => {
        e.preventDefault();
        setFieldValue('partnerIds', []);
    };
    if (partnersLoading || dataLoading) return <Loading />;
    return (
        <>
            <h2>Add to Queue</h2>
            {!!partners.length && (
                <p>Select the retailers that this title needs to be delivered to, then hit “Request Delivery”.</p>
            )}
            <p>
                Deliveries will occur within 1 day.{' '}
                {!isSpotifyUser && (
                    <em>Note that a partner can take up to 30 days (or sometimes longer) to make titles available.</em>
                )}
            </p>
            <form onSubmit={handleSubmit}>
                {!isSpotifyUser && (
                    <>
                        <fieldset className="mb-2">
                            <legend>Partners</legend>
                            {errors.partnerIds && touched.partnerIds && (
                                <div className="form-error standalone">
                                    <span>{errors.partnerIds}</span>
                                </div>
                            )}

                            {partners.map((partner, index) => (
                                <Checkbox
                                    key={partner.id}
                                    name="partnerIds"
                                    label={partner.name}
                                    id={partner.id}
                                    value={partner.id}
                                    checked={values.partnerIds.includes(partner.id)}
                                    onChange={handleChange}
                                    disabled={isPushing}
                                    className={classNames({
                                        'mb-2': index !== partnerIds.length - 1,
                                        'mb-0': index === partnerIds.length - 1
                                    })}
                                />
                            ))}
                        </fieldset>

                        <div>
                            {values.partnerIds.length !== partnerIds?.length && (
                                <button className="btn btn-link p-0 mb-3" type="button" onClick={handleSelectAll}>
                                    Select all
                                </button>
                            )}
                            {values.partnerIds.length === partnerIds?.length && (
                                <button className="btn btn-link p-0 mb-3" type="button" onClick={handleDeselectAll}>
                                    Deselect all
                                </button>
                            )}
                        </div>
                    </>
                )}
                <div>
                    <StatusButton
                        className="mr-4"
                        successState={submitSuccess}
                        successLabel="Added to Queue!"
                        label="Request Delivery"
                        disabled={isPushing}
                    />
                    <button className="btn btn-secondary" type="button" onClick={handleReset} disabled={isPushing}>
                        Cancel
                    </button>
                </div>
            </form>
        </>
    );
};

const PushDistributionView = ({ contentId }) => {
    const [showAddQueueModal, setShowAddQueueModal] = useState(false);
    const { loading: partnersLoading, data: partners } = useQuery(PUSH_PARTNERS);
    const { loading: dataLoading, data: pushApiStatus } = useQuery(PUSH_API_STATUS, {
        variables: {
            contentId
        }
    });
    const isSpotifyUser = usePermissions(userGroups.SPOTIFY);
    const errors = pushApiStatus?.pushApiTitleStatus?.messages?.filter((m) => m.is_error);
    const warnings = pushApiStatus?.pushApiTitleStatus?.messages?.filter((m) => !m.is_error);

    let targets = pushApiStatus?.pushApiTitleStatus?.partners;

    if (isSpotifyUser && targets?.length) {
        targets = targets.filter((t) => t.partner_id === 'spotify');
    }

    if (partnersLoading || dataLoading) return <Loading />;

    return (
        <>
            <div>
                <h3 className="mb-6">Push Distribution</h3>
                <p>
                    <a
                        href="https://support.gateway.audioengine.io/support/solutions/folders/5000307997"
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        Visit the help desk for documentation and troubleshooting.
                    </a>
                </p>
            </div>

            {isSpotifyUser && (!!errors?.length || !!warnings?.length) && (
                <div className="flash flash-info mb-4">
                    <p>
                        Error and warning messages apply to any Audiobook platform serviced by Findaway, and may not
                        apply to Spotify.
                    </p>
                </div>
            )}

            {!!errors?.length && (
                <div className="flash flash-danger mb-4">
                    <h6>Errors: These issues will prevent delivery to the indicated partners</h6>
                    <hr className="my-2" />
                    <ul className="m-0 pl-5">
                        {errors.map((error) => (
                            <li key={error.path.join('-')}>
                                {error.message} ({error.path.join(',')})
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {!!warnings?.length && (
                <div className="flash flash-warning mb-4">
                    <h6>Warnings: These issues will not stop delivery</h6>
                    <hr className="my-2" />
                    <ul className="m-0 pl-5">
                        {warnings.map((error) => (
                            <li key={error.path.join('-')}>
                                {error.message} ({error.path.join(',')})
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <Modal label="Add to Queue" visible={showAddQueueModal} onClose={() => setShowAddQueueModal(false)}>
                <AddToQueue contentId={contentId} onCancel={() => setShowAddQueueModal(false)} />
            </Modal>

            {!targets?.length && <div className="flash flash-warning mb-6">No Push Distribution Data</div>}

            {!!targets?.length && (
                <>
                    <div className="mb-6">
                        <button className="btn btn-primary" type="button" onClick={() => setShowAddQueueModal(true)}>
                            Add to Queue
                        </button>
                    </div>
                    <div className="table-responsive mb-6">
                        <table className="table table-sm full-width">
                            <thead>
                                <tr className="bg-light">
                                    <th>Partner</th>
                                    <th>
                                        Target ID{' '}
                                        <FaInfoCircle data-rh="This is the identifier that the partner would recognize for this title" />
                                    </th>
                                    <th>
                                        Metadata
                                        <br /> Status
                                    </th>
                                    <th>
                                        Metadata
                                        <br /> Last Delivered
                                    </th>
                                    <th>
                                        Asset
                                        <br /> Status
                                    </th>
                                    <th>
                                        Asset
                                        <br /> Last Delivered
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {targets.map((target) => {
                                    const _partner = partners?.pushApiPartners?.find((p) => p.id === target.partner_id);
                                    return (
                                        <tr key={target.partner_id}>
                                            <th>{_partner?.name || target.partner_id}</th>
                                            <td data-title="Target ID:">{target.target_id}</td>
                                            {/* Metadata */}
                                            <td data-title="Metadata Status:">{target.metadata.summary}</td>
                                            <td data-title="Metadata Last Delivered:">
                                                {!!target?.metadata?.last_sent &&
                                                    format(parseISO(target.metadata.last_sent), 'P')}
                                            </td>
                                            {/* Audio */}
                                            <td data-title="Assets Status:">{target.assets.summary}</td>
                                            <td data-title="Assets Last Devlivered:">
                                                {!!target?.assets?.last_sent &&
                                                    format(parseISO(target.assets.last_sent), 'P')}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            <div>
                <h4>Status Glossary</h4>
                <div className="table-responsive">
                    <table className="table full-width table-sm">
                        <thead>
                            <tr className="bg-light">
                                <th>Status</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>UP_TO_DATE</td>
                                <td>
                                    This book has an active product and Findaway has delivered the most recent version
                                    of this book to the partner. Partner ingestion times vary.
                                </td>
                            </tr>
                            <tr>
                                <td>UPDATE_QUEUED</td>
                                <td>Update is packaged and awaiting the next scheduled upload.</td>
                            </tr>
                            <tr>
                                <td>NEEDS_UPDATE</td>
                                <td>
                                    Findaway has a newer version of this title but has not delivered or queued for
                                    delivery yet.
                                </td>
                            </tr>
                            <tr>
                                <td>NEEDS_TAKEDOWN</td>
                                <td>
                                    This title does not have active products. Takedown request has not been sent or
                                    queued.
                                </td>
                            </tr>
                            <tr>
                                <td>TAKEDOWN_QUEUED</td>
                                <td>A takedown will be sent at the next scheduled upload.</td>
                            </tr>
                            <tr>
                                <td>TAKEN_DOWN</td>
                                <td>
                                    The last delivery to partner included a takedown notice. Partner ingestion times
                                    vary.
                                </td>
                            </tr>
                            <tr>
                                <td>DATA_NOT_FOUND</td>
                                <td>
                                    This book has never had products for this partner. This may be the result of not
                                    having the rights or missing critical information.
                                </td>
                            </tr>
                            <tr>
                                <td>PREORDER_SENT_WITHOUT_AUDIO</td>
                                <td>
                                    The metadata for this book has been sent as a pre-order, but the audio has not been
                                    delivered (typically because Findaway does not have it).
                                </td>
                            </tr>
                            <tr>
                                <td>NOT_REQUIRED</td>
                                <td>
                                    Delivering metadata was not required because Spotify receives metadata via the
                                    AudioEngine API
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
};

export default PushDistributionView;
